import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import collectionChargeService from '../services/collectionChargeService';
import masterService from '../services/masterService';
import ConfirmModal from './masters/components/ConfirmModal';
import {
  MODES, COLLECTION_TYPES, BASES, labelOf, emptyRuleForm, ruleToForm, validateRuleForm, toRulePayload,
  formatMoney, describeApiError,
} from '../utils/collectionCharge';
import './masters/MastersHub.css';
import './CollectionChargePage.css';

const PAGE_SIZE = 10;
const EMPTY_FILTERS = { centreId: '', mode: '', collectionType: '', isActive: '', search: '' };

export const CollectionChargePage = () => {
  const { can, user } = useAuth();
  const canCreate = can('COLLECTION_CHARGE', 'CREATE');
  const canUpdate = can('COLLECTION_CHARGE', 'UPDATE');
  const canDelete = can('COLLECTION_CHARGE', 'DELETE');

  const [centres, setCentres] = useState([]);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [applied, setApplied] = useState(EMPTY_FILTERS);
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ items: [], totalCount: 0 });
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState(null);

  const [editor, setEditor] = useState(null); // { mode: 'create' | 'edit' | 'view', form }
  const [formErrors, setFormErrors] = useState([]);
  const errorRef = useRef(null);
  useEffect(() => {
    if (formErrors.length && errorRef.current?.scrollIntoView) errorRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [formErrors]);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState({ orderValue: '500', manualAmount: '', overrideAmount: '', overrideReason: '', waive: false, waiverReason: '', result: null, error: null, busy: false });

  const [confirm, setConfirm] = useState(null); // { rule, activate }
  const [history, setHistory] = useState(null); // { rule, items }

  // The centre list is an administrator API; a centre-scoped user simply sees their own centre.
  useEffect(() => {
    masterService.getCentres({ isActive: true })
      .then((res) => {
        if (res.success && res.data?.length) setCentres(res.data);
        else throw new Error('empty');
      })
      .catch(() => {
        if (user?.centreId) setCentres([{ centreId: user.centreId, centreName: user.centreName || `Centre ${user.centreId}` }]);
      });
  }, [user]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, pageSize: PAGE_SIZE };
      if (applied.centreId) params.centreId = applied.centreId;
      if (applied.mode) params.mode = applied.mode;
      if (applied.collectionType) params.collectionType = applied.collectionType;
      if (applied.isActive !== '') params.isActive = applied.isActive === 'true';
      if (applied.search.trim()) params.search = applied.search.trim();
      const res = await collectionChargeService.listRules(params);
      if (res.success) setData(res.data);
      else setMessage({ type: 'error', text: res.message || 'Failed to load collection charges.' });
    } catch (err) {
      setMessage({ type: 'error', text: describeApiError(err, 'Failed to load collection charges.') });
    } finally {
      setLoading(false);
    }
  }, [applied, page]);

  useEffect(() => { load(); }, [load]);

  const totalPages = Math.max(1, Math.ceil((data.totalCount || 0) / PAGE_SIZE));

  const applyFilters = (e) => {
    e?.preventDefault();
    setPage(1);
    setApplied(filters);
  };

  const resetFilters = () => {
    setFilters(EMPTY_FILTERS);
    setApplied(EMPTY_FILTERS);
    setPage(1);
  };

  const openCreate = () => {
    setEditor({ mode: 'create', form: emptyRuleForm(centres.length === 1 ? centres[0].centreId : user?.centreId || '') });
    setFormErrors([]);
    setPreview((p) => ({ ...p, result: null, error: null }));
  };

  const openRule = async (rule, mode) => {
    try {
      const res = await collectionChargeService.getRule(rule.ruleId); // always edit the latest version
      setEditor({ mode, ruleId: res.data.ruleId, form: ruleToForm(res.data) });
      setFormErrors([]);
      setPreview((p) => ({ ...p, result: null, error: null }));
    } catch (err) {
      setMessage({ type: 'error', text: describeApiError(err, 'Could not open the rule.') });
    }
  };

  const setField = (name, value) => {
    setEditor((e) => ({ ...e, form: { ...e.form, [name]: value } }));
    setPreview((p) => ({ ...p, result: null, error: null }));
  };

  const handleSave = async () => {
    if (saving || !editor || editor.mode === 'view') return;
    const errors = validateRuleForm(editor.form);
    setFormErrors(errors);
    if (errors.length) return;
    setSaving(true);
    try {
      const payload = toRulePayload(editor.form);
      const res = editor.mode === 'create'
        ? await collectionChargeService.createRule(payload)
        : await collectionChargeService.updateRule(editor.ruleId, payload);
      if (res.success) {
        setMessage({ type: 'success', text: editor.mode === 'create' ? 'Collection charge rule created.' : 'Collection charge rule updated.' });
        setEditor(null);
        await load();
      } else {
        setFormErrors([res.message || 'Could not save the rule.']);
      }
    } catch (err) {
      setFormErrors([describeApiError(err, 'Could not save the rule.')]);
    } finally {
      setSaving(false);
    }
  };

  const runPreview = async () => {
    if (preview.busy || !editor) return;
    const errors = validateRuleForm(editor.form);
    setFormErrors(errors);
    if (errors.length) return;
    setPreview((p) => ({ ...p, busy: true, result: null, error: null }));
    try {
      const res = await collectionChargeService.previewRule({
        rule: toRulePayload(editor.form),
        orderValue: Number(preview.orderValue) || 0,
        manualAmount: preview.manualAmount === '' ? null : Number(preview.manualAmount),
        overrideAmount: preview.overrideAmount === '' ? null : Number(preview.overrideAmount),
        overrideReason: preview.overrideReason || null,
        waive: preview.waive,
        waiverReason: preview.waiverReason || null,
      });
      setPreview((p) => ({ ...p, result: res.data, busy: false }));
    } catch (err) {
      setPreview((p) => ({ ...p, error: describeApiError(err, 'Preview failed.'), busy: false }));
    }
  };

  const runConfirm = async () => {
    if (!confirm || saving) return;
    setSaving(true);
    try {
      const { rule, activate } = confirm;
      const res = activate
        ? await collectionChargeService.activateRule(rule.ruleId, rule.rowVersion)
        : await collectionChargeService.deactivateRule(rule.ruleId, rule.rowVersion);
      setMessage({ type: res.success ? 'success' : 'error', text: res.message });
    } catch (err) {
      setMessage({ type: 'error', text: describeApiError(err, 'Could not change the rule status.') });
    } finally {
      setSaving(false);
      setConfirm(null);
      await load();
    }
  };

  const openHistory = async (rule) => {
    try {
      const res = await collectionChargeService.listAudit({ ruleId: rule.ruleId, pageSize: 50 });
      setHistory({ rule, items: res.data?.items || [] });
    } catch (err) {
      setMessage({ type: 'error', text: describeApiError(err, 'Could not load the history.') });
    }
  };

  const centreName = (id) => centres.find((c) => String(c.centreId) === String(id))?.centreName || `Centre ${id}`;
  const readOnly = editor?.mode === 'view';
  const f = editor?.form;

  return (
    <div className="ccm-container">
      <header className="ccm-header">
        <div>
          <h1>Collection Charge Master</h1>
          <p>Centre-wise rules for the collection charge billed separately from tests. Changes apply to new bills only; existing bills keep the charge they were billed.</p>
        </div>
        {canCreate && <button type="button" className="ccm-primary" onClick={openCreate}>+ New rule</button>}
      </header>

      {message && (
        <div className={`ccm-msg ccm-msg--${message.type}`} role={message.type === 'error' ? 'alert' : 'status'}>{message.text}</div>
      )}

      <form className="ccm-filters" onSubmit={applyFilters} aria-label="Filters">
        <select aria-label="Filter centre" value={filters.centreId} onChange={(e) => setFilters({ ...filters, centreId: e.target.value })}>
          <option value="">All centres</option>
          {centres.map((c) => <option key={c.centreId} value={c.centreId}>{c.centreName}</option>)}
        </select>
        <select aria-label="Filter mode" value={filters.mode} onChange={(e) => setFilters({ ...filters, mode: e.target.value })}>
          <option value="">All modes</option>
          {MODES.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
        </select>
        <select aria-label="Filter type" value={filters.collectionType} onChange={(e) => setFilters({ ...filters, collectionType: e.target.value })}>
          <option value="">All collection types</option>
          {COLLECTION_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
        </select>
        <select aria-label="Filter status" value={filters.isActive} onChange={(e) => setFilters({ ...filters, isActive: e.target.value })}>
          <option value="">Active and inactive</option>
          <option value="true">Active</option>
          <option value="false">Inactive</option>
        </select>
        <input aria-label="Search" placeholder="Search area, pincode, remarks" value={filters.search} maxLength={50} onChange={(e) => setFilters({ ...filters, search: e.target.value })} />
        <button type="submit">Search</button>
        <button type="button" onClick={resetFilters}>Reset</button>
      </form>

      <div className="ccm-card">
        <div className="ccm-table-wrap">
          <table className="ccm-table">
            <thead>
              <tr>
                <th>Centre</th><th>Type</th><th>Mode</th><th>Basis</th><th>Amount</th><th>Area</th><th>Effective</th><th>Status</th><th>v</th><th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={10} className="ccm-empty">Loading…</td></tr>}
              {!loading && data.items.length === 0 && <tr><td colSpan={10} className="ccm-empty">No collection charge rules found.</td></tr>}
              {!loading && data.items.map((r) => (
                <tr key={r.ruleId} className={r.isActive ? '' : 'is-inactive'}>
                  <td>{r.centreName || centreName(r.centreId)}</td>
                  <td>{labelOf(COLLECTION_TYPES, r.collectionType)}</td>
                  <td><span className={`ccm-mode ccm-mode--${r.mode.toLowerCase()}`}>{labelOf(MODES, r.mode)}</span></td>
                  <td>{r.chargeBasis === 'VISIT' ? 'Per visit' : 'Per order'}</td>
                  <td>
                    {r.mode === 'NO_CHARGE' ? '—' : r.mode === 'MANUAL' ? `up to ${formatMoney(r.maxManualAmount)}` : formatMoney(r.defaultAmount)}
                    {r.freeAboveOrderValue != null && <small className="ccm-sub">free ≥ {formatMoney(r.freeAboveOrderValue)}</small>}
                  </td>
                  <td>{r.pinCode ? `${r.pinCode}${r.areaName ? ` · ${r.areaName}` : ''}` : 'Centre default'}</td>
                  <td>{r.effectiveFrom?.slice(0, 10)} → {r.effectiveTo ? r.effectiveTo.slice(0, 10) : 'open'}</td>
                  <td><span className={`ccm-badge ${r.isActive ? 'is-on' : 'is-off'}`}>{r.isActive ? 'Active' : 'Inactive'}</span></td>
                  <td>{r.rowVersion}</td>
                  <td className="ccm-actions">
                    <button type="button" onClick={() => openRule(r, 'view')}>View</button>
                    {canUpdate && <button type="button" onClick={() => openRule(r, 'edit')}>Edit</button>}
                    {r.isActive && canDelete && <button type="button" onClick={() => setConfirm({ rule: r, activate: false })}>Deactivate</button>}
                    {!r.isActive && canUpdate && <button type="button" onClick={() => setConfirm({ rule: r, activate: true })}>Activate</button>}
                    <button type="button" onClick={() => openHistory(r)}>History</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="ccm-pager">
          <button type="button" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Previous</button>
          <span>Page {page} of {totalPages} · {data.totalCount} rules</span>
          <button type="button" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>Next</button>
        </div>
      </div>

      {editor && (
        <div className="mst-modal-backdrop" onClick={() => !saving && setEditor(null)}>
          <div className="mst-modal-card ccm-dialog" role="dialog" aria-modal="true" aria-label="Collection charge rule" onClick={(e) => e.stopPropagation()}>
            <h3 className="mst-modal-title">
              {editor.mode === 'create' ? 'New collection charge rule' : editor.mode === 'edit' ? 'Edit collection charge rule' : 'Collection charge rule'}
            </h3>

            {formErrors.length > 0 && (
              <div className="ccm-alert" role="alert" ref={errorRef}>
                <strong>{formErrors.length > 1 ? "Please fix these problems before saving:" : "Please fix this before saving:"}</strong>
                <ul>{formErrors.map((e) => <li key={e}>{e}</li>)}</ul>
              </div>
            )}

            <div className="ccm-form">
              <section className="ccm-section">
                <h4><span className="ccm-step">1</span> Where does this rule apply?</h4>
                <div className="ccm-grid">
                  <label>Centre
                    <select aria-label="Centre" value={f.centreId} disabled={readOnly || editor.mode === 'edit'} onChange={(e) => setField('centreId', e.target.value)}>
                      <option value="">Select centre</option>
                      {centres.map((c) => <option key={c.centreId} value={c.centreId}>{c.centreName}</option>)}
                    </select>
                    <small className="ccm-hint">The lab centre whose bills get this charge. Cannot be changed after saving.</small>
                  </label>
                  <label>Collection type
                    <select aria-label="Collection type" value={f.collectionType} disabled={readOnly} onChange={(e) => setField('collectionType', e.target.value)}>
                      {COLLECTION_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                    </select>
                    <small className="ccm-hint">How the sample is collected: home, walk-in, camp or other. Registration visit type decides which rule is used.</small>
                  </label>
                  <label>Pincode <em>(optional)</em>
                    <input aria-label="Pincode" inputMode="numeric" maxLength={6} placeholder="e.g. 560001" value={f.pinCode} disabled={readOnly} onChange={(e) => setField('pinCode', e.target.value.replace(/\D/g, ''))} />
                    <small className="ccm-hint">Leave empty for the centre default. A pincode rule overrides the default for patients of that area.</small>
                  </label>
                  <label>Area name <em>(optional)</em>
                    <input aria-label="Area name" maxLength={100} placeholder="e.g. Indiranagar" value={f.areaName} disabled={readOnly} onChange={(e) => setField('areaName', e.target.value)} />
                    <small className="ccm-hint">Only a label to recognise the area rule in the list.</small>
                  </label>
                </div>
              </section>

              <section className="ccm-section">
                <h4><span className="ccm-step">2</span> How is the charge decided?</h4>
                <div className="ccm-grid">
                  <label>Charge mode
                    <select aria-label="Charge mode" value={f.mode} disabled={readOnly} onChange={(e) => setField('mode', e.target.value)}>
                      {MODES.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
                    </select>
                    <small className="ccm-hint">Automatic: fixed amount. Manual: cashier types it. Auto + override: fixed, changeable by authorised staff. No charge: never charged.</small>
                  </label>
                  <label>Charged
                    <select aria-label="Charge basis" value={f.chargeBasis} disabled={readOnly} onChange={(e) => setField('chargeBasis', e.target.value)}>
                      {BASES.map((b) => <option key={b.value} value={b.value}>{b.label}</option>)}
                    </select>
                    <small className="ccm-hint">Per order: every bill pays it. Per visit: charged once for the same patient, centre, day and type.</small>
                  </label>
                </div>
                <p className="ccm-help">{MODES.find((m) => m.value === f.mode)?.help}</p>
              </section>

              <section className="ccm-section">
                <h4><span className="ccm-step">3</span> Amounts and limits</h4>
                <div className="ccm-grid">
                  <label>Fixed amount (₹)
                    <input aria-label="Fixed amount" inputMode="decimal" value={f.defaultAmount} disabled={readOnly || f.mode === 'MANUAL' || f.mode === 'NO_CHARGE'} onChange={(e) => setField('defaultAmount', e.target.value)} />
                    <small className="ccm-hint">The charge added to the bill. Not used in Manual or No charge mode.</small>
                  </label>
                  <label>Maximum manual / override amount (₹)
                    <input aria-label="Maximum manual amount" inputMode="decimal" value={f.maxManualAmount} disabled={readOnly || f.mode === 'AUTOMATIC' || f.mode === 'NO_CHARGE'} onChange={(e) => setField('maxManualAmount', e.target.value)} />
                    <small className="ccm-hint">Highest amount a cashier may type (Manual) or an authorised user may set (Auto + override).</small>
                  </label>
                  <label>Free when test total is at least (₹)
                    <input aria-label="Free above" inputMode="decimal" placeholder="not set" value={f.freeAboveOrderValue} disabled={readOnly || f.mode === 'MANUAL' || f.mode === 'NO_CHARGE'} onChange={(e) => setField('freeAboveOrderValue', e.target.value)} />
                    <small className="ccm-hint">Optional. If the tests total this much or more, the charge becomes ₹0.</small>
                  </label>
                  <label className="ccm-check">
                    <input aria-label="Allow waiver" type="checkbox" checked={f.allowWaiver} disabled={readOnly || f.mode === 'NO_CHARGE'} onChange={(e) => setField('allowWaiver', e.target.checked)} />
                    <span>Allow waiver
                      <small className="ccm-hint">Lets staff with the Waive permission remove the charge on a bill, with a reason.</small>
                    </span>
                  </label>
                </div>
              </section>

              <section className="ccm-section">
                <h4><span className="ccm-step">4</span> When is it valid?</h4>
                <div className="ccm-grid">
                  <label>Effective from
                    <input aria-label="Effective from" type="date" value={f.effectiveFrom} disabled={readOnly} onChange={(e) => setField('effectiveFrom', e.target.value)} />
                    <small className="ccm-hint">First day the rule applies to new bills.</small>
                  </label>
                  <label>Effective to <em>(optional)</em>
                    <input aria-label="Effective to" type="date" value={f.effectiveTo} disabled={readOnly} onChange={(e) => setField('effectiveTo', e.target.value)} />
                    <small className="ccm-hint">Last day. Leave empty for no end date.</small>
                  </label>
                  <label className="ccm-wide">Remarks <em>(optional)</em>
                    <input aria-label="Remarks" maxLength={200} placeholder="Why this rule exists" value={f.remarks} disabled={readOnly} onChange={(e) => setField('remarks', e.target.value)} />
                    <small className="ccm-hint">Internal note shown in the history.</small>
                  </label>
                </div>
              </section>
            </div>


            <fieldset className="ccm-preview">
              <legend>Preview with example values (nothing is saved)</legend>
              <div className="ccm-preview-row">
                <label>Example test total (₹)<input aria-label="Example order value" inputMode="decimal" value={preview.orderValue} onChange={(e) => setPreview({ ...preview, orderValue: e.target.value, result: null })} /></label>
                {f.mode === 'MANUAL' && (
                  <label>Manual amount (₹)<input aria-label="Preview manual amount" inputMode="decimal" value={preview.manualAmount} onChange={(e) => setPreview({ ...preview, manualAmount: e.target.value, result: null })} /></label>
                )}
                {f.mode === 'AUTO_OVERRIDE' && (
                  <>
                    <label>Override amount (₹)<input aria-label="Preview override amount" inputMode="decimal" value={preview.overrideAmount} onChange={(e) => setPreview({ ...preview, overrideAmount: e.target.value, result: null })} /></label>
                    <label>Reason<input aria-label="Preview override reason" value={preview.overrideReason} onChange={(e) => setPreview({ ...preview, overrideReason: e.target.value, result: null })} /></label>
                  </>
                )}
                {f.allowWaiver && f.mode !== 'NO_CHARGE' && (
                  <label className="ccm-check"><input type="checkbox" aria-label="Preview waive" checked={preview.waive} onChange={(e) => setPreview({ ...preview, waive: e.target.checked, result: null })} />Waive</label>
                )}
                {preview.waive && <label>Waiver reason<input aria-label="Preview waiver reason" value={preview.waiverReason} onChange={(e) => setPreview({ ...preview, waiverReason: e.target.value, result: null })} /></label>}
                <button type="button" onClick={runPreview} disabled={preview.busy}>{preview.busy ? 'Calculating…' : 'Preview'}</button>
              </div>
              {preview.result && (
                <div className="ccm-preview-result" role="status">
                  Calculated {formatMoney(preview.result.calculatedAmount)} → <strong>charge {formatMoney(preview.result.finalAmount)}</strong>
                  {preview.result.note ? <small className="ccm-sub">{preview.result.note}</small> : null}
                </div>
              )}
              {preview.error && <div className="ccm-preview-result is-error" role="alert">{preview.error}</div>}
            </fieldset>

            <div className="mst-modal-footer">
              <button type="button" onClick={() => setEditor(null)} disabled={saving}>{readOnly ? 'Close' : 'Cancel'}</button>
              {!readOnly && (
                <button type="button" className="ccm-primary" onClick={handleSave} disabled={saving}>
                  {saving ? 'Saving…' : editor.mode === 'create' ? 'Create rule' : 'Save changes'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {history && (
        <div className="mst-modal-backdrop" onClick={() => setHistory(null)}>
          <div className="mst-modal-card ccm-dialog" role="dialog" aria-modal="true" aria-label="Audit history" onClick={(e) => e.stopPropagation()}>
            <h3 className="mst-modal-title">History: rule #{history.rule.ruleId}</h3>
            {history.items.length === 0 ? <p>No history recorded.</p> : (
              <ol className="ccm-history">
                {history.items.map((h) => (
                  <li key={h.auditId}>
                    <strong>{h.action}</strong> · {new Date(h.actionDate).toLocaleString()} · {h.actor || 'unknown'}
                    {h.oldValue && <details><summary>before / after</summary><pre>{h.oldValue}{'\n'}{h.newValue}</pre></details>}
                  </li>
                ))}
              </ol>
            )}
            <div className="mst-modal-footer"><button type="button" onClick={() => setHistory(null)}>Close</button></div>
          </div>
        </div>
      )}

      <ConfirmModal
        isOpen={!!confirm}
        title={confirm?.activate ? 'Activate rule' : 'Deactivate rule'}
        message={confirm?.activate
          ? 'This rule will apply to new bills again. Bills already billed are not affected.'
          : 'New bills will no longer use this rule. Bills already billed keep the charge they were billed.'}
        confirmLabel={confirm?.activate ? 'Activate' : 'Deactivate'}
        variant={confirm?.activate ? 'primary' : 'danger'}
        loading={saving}
        onConfirm={runConfirm}
        onCancel={() => !saving && setConfirm(null)}
      />
    </div>
  );
};

export default CollectionChargePage;
